//* Import API configurations
import { api } from "../../services/apis/configs/axiosConfigs";

//* Import API URI constant
import { API_URI } from "../../constants/consts";

//* Import PaginationData type
import { type Pagination } from "../../services/stores/types";

//* Import types from types
import {
  type Test,
  type TestDetails,
  type TestResponse,
} from "./types/types";

// API methods for Test operations
export const testAPI = {
  // Fetch all Tests with optional search and pagination
  getAll: async function (
    search: string = "",
    page: number = 1,
    token: string,
    perPage: number = 10
  ): Promise<TestResponse> {
    const searchQuery = search ? `&search=${search}` : "";
    const url = `${API_URI}/admin/private/tests?page=${page}&perPage=${perPage}${searchQuery}`;

    return api
      .request({
        url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const { data } = response.data;

        const pagination: Pagination = data.pagination
          ? {
              currentPage: data.pagination.currentPage,
              totalPages: data.pagination.totalPages,
              itemsPerPage: data.pagination.itemsPerPage,
              totalItems: data.pagination.totalItems,
            }
          : {
              currentPage: 0,
              totalPages: 0,
              itemsPerPage: 0,
              totalItems: 0,
            };

        return {
          test: data.test || [],
          pagination,
        };
      })
      .catch((error) => {
        console.error("Error fetching Tests:", error);
        throw new Error("Failed to fetch Tests");
      });
  },

  // Fetch a Test by ID
  getById: async function (
    id: string,
    token: string
  ): Promise<TestDetails> {
    return api
      .request({
        url: `${API_URI}/admin/private/tests/${id}`,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        return response.data.data;
      })
      .catch((error) => {
        console.error("Error fetching test:", error);
        throw new Error("Failed to fetch Test");
      });
  },

  // Create a new Test
  create: async function (
    data: { name: string , description?: string, type: string},
    token: string
  ): Promise<Test> {
    return api
      .request({
        url: `${API_URI}/admin/private/tests`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data,
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Error creating test:", error, data);
        throw new Error("Failed to create Test");
      });
  },

  // Update an existing Test
  update: async function (
    id: string,
    data: { name: string , description?: string, type: string},
    token: string
  ): Promise<Test> {
    return api
      .request({
        url: `${API_URI}/admin/private/tests/${id}`,
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data,
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Error updating test:", error);
        throw new Error("Failed to update Test");
      });
  },

  // Delete an Test by ID
  delete: async function (id: string, token: string): Promise<void> {
    return api
      .request({
        url: `${API_URI}/admin/private/tests/${id}`,
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {})
      .catch((error) => {
        console.error("Error deleting test:", error);
        throw new Error("Failed to delete Test");
      });
  },
};
